import {
  createDefaultModelSubscriptionSwitches,
  createDefaultModelSubscriptionValues,
} from '@/components-biz/ModelSubscriptionForm/shared';
import type { ModelSubscriptionResourceSwitches } from '@/components-biz/ModelSubscriptionForm/types';

import { MS_PROVIDER_PRICING_FIELD } from '../../../capabilities/model-subscription-provider/constants';
import { deriveMsUseSubResourcePricing } from '../../../capabilities/model-subscription-provider/useSubResourcePricingField';
import { readVendorDeviceMeta } from '../../../components/vendorDevice';
import type { PipelineContext } from '../../../core/types';
import { resolveEnabledModulesForSpecFilter } from '../../_shared/common/sku/resolveEnabledModulesForSpecFilter';
import { modelSubscriptionFormSwitchesToFields } from '../types/detail';
import { filterMsSpecByEnabledModules } from './enabledModules';
import { buildMsSubscriptionPayload, readMsSwitchesFromForm } from './payload';
import type { MsSpec } from './schema';

function switchesToFormFields(switches: ModelSubscriptionResourceSwitches) {
  return modelSubscriptionFormSwitchesToFields(switches);
}

export function msFromForm(values: Record<string, unknown>, ctx: PipelineContext): MsSpec {
  const switches = readMsSwitchesFromForm(values);
  const payload = buildMsSubscriptionPayload(values, {
    ...switches,
    creator: ctx.overlay?.creator ? String(ctx.overlay.creator) : undefined,
  });

  const providerPricing = readVendorDeviceMeta(values, MS_PROVIDER_PRICING_FIELD);

  const spec: MsSpec = {
    alias: payload.alias,
    description: payload.description,
    workspace: payload.workspace,
    creator: payload.creator,
    allowedClientCIDRs: payload.allowedClientCIDRs,
    allowedClientIPs: payload.allowedClientIPs,
    strategyName: payload.llmgwStrategyRef.external.strategyName || undefined,
    strategyDisplayName: values.strategyDisplayName != null ? String(values.strategyDisplayName) : undefined,
    strategyProvider: values.strategyProvider != null ? String(values.strategyProvider) : undefined,
    providerPricing: providerPricing || undefined,
    useSubResourcePricing: deriveMsUseSubResourcePricing({
      useSubResourcePricing: values.useSubResourcePricing,
      billingTotalTokens: values.billingTotalTokens,
      inputTokens: values.inputTokens,
      outputTokens: values.outputTokens,
    }),
    llmgwStrategyRef: payload.llmgwStrategyRef,
    rateLimits: payload.rateLimits,
    resourceLimits: payload.resourceLimits,
    switches,
  };

  const enabledModules = resolveEnabledModulesForSpecFilter(ctx, values);
  return filterMsSpecByEnabledModules(spec, enabledModules, ctx.sku?.skuType);
}

function deriveSwitchesFromSpec(spec: MsSpec): ModelSubscriptionResourceSwitches {
  if (spec.switches) return { ...spec.switches, showTokenLimit: false };
  const requests = spec.resourceLimits?.requests;
  const inputTokenRateLimit = spec.rateLimits?.inputTokens;
  const outputTokenRateLimit = spec.rateLimits?.outputTokens;
  return {
    showTokenLimit: false,
    showRequests: Boolean(requests?.enabled),
    showInputTokens: Boolean(inputTokenRateLimit?.enabled),
    showOutputTokens: Boolean(outputTokenRateLimit?.enabled),
  };
}

export function msToForm(spec: MsSpec): Record<string, unknown> {
  const switches = deriveSwitchesFromSpec(spec);
  return {
    alias: spec.alias,
    description: spec.description ?? '',
    workspace: spec.workspace,
    strategyName: spec.strategyName ?? spec.llmgwStrategyRef?.external?.strategyName,
    strategyDisplayName: spec.strategyDisplayName ?? '',
    strategyProvider: spec.strategyProvider ?? '',
    ...(spec.providerPricing ? { [MS_PROVIDER_PRICING_FIELD]: spec.providerPricing } : {}),
    useSubResourcePricing: deriveMsUseSubResourcePricing({
      useSubResourcePricing: spec.useSubResourcePricing,
      billingTotalTokens: spec.resourceLimits?.billingTotalTokens?.enabled ? spec.resourceLimits.billingTotalTokens.value : undefined,
      inputTokens: spec.resourceLimits?.inputTokens?.enabled ? spec.resourceLimits.inputTokens.value : undefined,
      outputTokens: spec.resourceLimits?.outputTokens?.enabled ? spec.resourceLimits.outputTokens.value : undefined,
    }),
    allowedClientCIDRs: spec.allowedClientCIDRs?.length ? spec.allowedClientCIDRs : ['0.0.0.0/0'],
    allowedClientIPs: spec.allowedClientIPs?.length ? spec.allowedClientIPs : [''],
    billingTotalTokens: spec.resourceLimits?.billingTotalTokens?.enabled ? spec.resourceLimits.billingTotalTokens.value : undefined,
    inputTokens: spec.resourceLimits?.inputTokens?.enabled ? spec.resourceLimits.inputTokens.value : undefined,
    outputTokens: spec.resourceLimits?.outputTokens?.enabled ? spec.resourceLimits.outputTokens.value : undefined,
    requests: spec.resourceLimits?.requests?.enabled ? spec.resourceLimits.requests.value : undefined,
    inputTokensValue: spec.rateLimits?.inputTokens?.enabled ? spec.rateLimits.inputTokens.value : undefined,
    inputTokensWindowSeconds: spec.rateLimits?.inputTokens?.enabled ? spec.rateLimits.inputTokens.windowSeconds : undefined,
    outputTokensValue: spec.rateLimits?.outputTokens?.enabled ? spec.rateLimits.outputTokens.value : undefined,
    outputTokensWindowSeconds: spec.rateLimits?.outputTokens?.enabled ? spec.rateLimits.outputTokens.windowSeconds : undefined,
    ...switchesToFormFields(switches),
  };
}

export function msGetInitialFormValues(_ctx?: PipelineContext): Record<string, unknown> {
  const defaults = createDefaultModelSubscriptionValues();
  const switches = createDefaultModelSubscriptionSwitches();
  return {
    ...defaults,
    ...switchesToFormFields(switches),
  };
}
